import { useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Badge from "react-bootstrap/Badge";
import Card from "react-bootstrap/Card";
import { StandalonePatentMap } from "./GeoPatentMap";
import "bootstrap/dist/css/bootstrap.min.css";

// Make sure this interface matches the one in DemoPatentMap.tsx
interface MapPatent {
  application_number: string;
  country_code: string;
  filing_date: string;
  legal_status: string;
}

export interface PatentDetails {
  title: string;
  patent_number: string;
  patent_status: string;
  priority_date: string;
  expiration_date: string;
  rank: number;
  url: string;
  applications: MapPatent[];
}

interface PatentDetailsModalProps {
  patent: PatentDetails;
  show: boolean;
  onHide: () => void;
}

export function PatentDetailsModal({
  patent,
  show,
  onHide,
}: PatentDetailsModalProps) {
  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [show]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      dialogClassName="patent-modal-wide"
      aria-labelledby="patent-details-modal"
    >
      <Modal.Header className="bg-primary text-white">
        <Modal.Title id="patent-details-modal">{patent.title}</Modal.Title>
        <Button
          variant="link"
          className="text-white close"
          onClick={onHide}
          aria-label="Close"
        >
          <span aria-hidden="true">&times;</span>
        </Button>
      </Modal.Header>

      <Modal.Body className="p-0">
        <Row className="m-0">
          {/* Map Component - 75% width on larger screens */}
          <Col xs={12} md={9} className="p-0" style={{ minHeight: "500px" }}>
            <StandalonePatentMap patents={patent.applications} />
          </Col>

          {/* Patent Details - 25% width on larger screens */}
          <Col xs={12} md={3} className="border-left p-0">
            <Card className="border-0 h-100">
              <Card.Header className="d-flex flex-column bg-white border-bottom">
                <span className="font-weight-bold mb-2">Patent Status</span>
                <Badge bg="primary">{patent.patent_status}</Badge>
              </Card.Header>
              <Card.Body className="px-3 py-2">
                <div className="patent-info-section">
                  <Card className="mb-3 shadow-sm">
                    <Card.Body className="py-2">
                      <small className="text-muted d-block">
                        Patent Number
                      </small>
                      <span className="font-weight-bold">
                        {patent.patent_number}
                      </span>
                    </Card.Body>
                  </Card>

                  <div className="d-flex mb-3">
                    <Card className="flex-grow-1 mr-2 shadow-sm">
                      <Card.Body className="py-2">
                        <small className="text-muted d-block">
                          Priority Date
                        </small>
                        <span className="font-weight-bold">
                          {patent.priority_date}
                        </span>
                      </Card.Body>
                    </Card>

                    <Card className="flex-grow-1 shadow-sm">
                      <Card.Body className="py-2">
                        <small className="text-muted d-block">Expiration</small>
                        <span className="font-weight-bold">
                          {patent.expiration_date}
                        </span>
                      </Card.Body>
                    </Card>
                  </div>

                  <Card className="mb-3 shadow-sm">
                    <Card.Body className="py-2">
                      <small className="text-muted d-block">Rank</small>
                      <div className="d-flex align-items-center">
                        <div
                          className="rank-indicator"
                          style={{
                            height: "8px",
                            width: `${Math.min(patent.rank * 10, 100)}%`,
                            backgroundColor: "#007bff",
                            borderRadius: "4px",
                          }}
                        ></div>
                        <span className="ml-2 font-weight-bold">
                          {patent.rank}
                        </span>
                      </div>
                    </Card.Body>
                  </Card>
                </div>

                <hr className="my-3" />

                <Button
                  variant="outline-primary"
                  className="w-100 d-flex align-items-center justify-content-center"
                  href={patent.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mr-2"
                  >
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                    <polyline points="15 3 21 3 21 9"></polyline>
                    <line x1="10" y1="14" x2="21" y2="3"></line>
                  </svg>
                  View on Google Patents
                </Button>

                <div className="mt-3 text-center">
                  <small className="text-muted">
                    Application Count: {patent.applications.length}
                  </small>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer className="border-top">
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// Export the StandalonePatentMap component to make it available to other files
export const DemoPatentMap = StandalonePatentMap;

// Add this CSS to your styles file
// .patent-modal-wide .modal-dialog {
//   max-width: 95%;
// }
//
// .patent-info-section .card:hover {
//   box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
//   transition: box-shadow 0.3s ease-in-out;
// }
